import { Box } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import type { FC } from "react";
import { ANNIE_STUDIO, MOTION } from "../../../theme/tokens";
import { AssistantAvatar } from "../../UIToolkit/AssistantAvatar/AssistantAvatar";
import { type AssistantAvatarConfig, AssistantAvatarMood } from "../../UIToolkit/AssistantAvatar/assistantAvatarConfig";

const POP_DURATION_S = MOTION.DURATION.POP / 1000;
const { FROM, OVERSHOOT } = ANNIE_STUDIO.POP_SCALE;

type Props = {
	draft: AssistantAvatarConfig;
	mood: AssistantAvatarMood;
	/** Bumps per design change: each one lands with a pop (none on the first frame). */
	revision: number;
	/** Bumps per mood pick, so re-picking a one-shot mood replays it. */
	moodRevision: number;
	size: number;
	isHidden: boolean;
};

/** The stage's large avatar: dimmed and still while "Show my Annie" is off. */
export const AnnieStudioPreview: FC<Props> = ({ draft, mood, revision, moodRevision, size, isHidden }) => {
	const reduceMotion = useReducedMotion();
	return (
		<Box
			sx={{
				opacity: isHidden ? ANNIE_STUDIO.HIDDEN_OPACITY : 1,
				filter: isHidden ? `grayscale(${ANNIE_STUDIO.HIDDEN_GRAYSCALE})` : "none",
				transition: `opacity ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, filter ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
			}}
		>
			<motion.div
				key={revision}
				initial={revision === 0 || reduceMotion ? false : { transform: `scale(${FROM})` }}
				animate={{ transform: reduceMotion ? "scale(1)" : [`scale(${FROM})`, `scale(${OVERSHOOT})`, "scale(1)"] }}
				transition={{ duration: POP_DURATION_S, ease: MOTION.EASE_OUT_POINTS }}
			>
				<AssistantAvatar
					key={moodRevision}
					config={draft}
					mood={isHidden ? AssistantAvatarMood.Idle : mood}
					size={size}
					animated={!isHidden}
					dataTestId="annie-studio-preview"
				/>
			</motion.div>
		</Box>
	);
};
